import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ParentChild {
  learnerId: string;
  schoolId: string | null;
  schoolName: string | null;
  currency: string;
  firstName: string;
  lastName: string;
  fullName: string;
  initials: string;
  photoUrl: string | null;
  gradeLabel: string | null;
  streamId: string | null;
  admissionNumber: string | null;
}

export interface ParentContextValue {
  parentUserId: string;
  parentIds: string[];
  children: ParentChild[];
}

function initialsOf(first: string, last: string) {
  return `${(first[0] ?? "").toUpperCase()}${(last[0] ?? "").toUpperCase()}` || "?";
}

export function useParentContext() {
  const [ctx, setCtx] = useState<ParentContextValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { if (mounted) setLoading(false); return; }

        const { data: parents } = await supabase
          .from("parents")
          .select("id, school_id")
          .eq("user_id", user.id);
        const parentIds = (parents ?? []).map((p) => p.id);

        let children: ParentChild[] = [];
        if (parentIds.length > 0) {
          const { data: links } = await supabase
            .from("parent_learner_links")
            .select("learner_id")
            .in("parent_id", parentIds);
          const learnerIds = Array.from(new Set((links ?? []).map((l) => l.learner_id)));

          if (learnerIds.length > 0) {
            const { data: ls } = await supabase
              .from("learners")
              .select(`
                id, school_id, current_stream_id, admission_number,
                first_name, last_name, photo_url,
                streams:current_stream_id(name, classes:class_id(grade, name)),
                schools:school_id(name, currency)
              `)
              .in("id", learnerIds);
            children = (ls ?? []).map((l) => {
              const s = (l as { streams?: { name?: string; classes?: { grade?: string; name?: string } | null } | null }).streams;
              const sch = (l as { schools?: { name?: string; currency?: string } | null }).schools;
              const gradeLabel =
                s?.classes ? `${s.classes.name ?? ""}${s.name ? ` · ${s.name}` : ""}`.trim() ||
                  (s.classes.grade ?? null) : null;
              return {
                learnerId: l.id,
                schoolId: l.school_id ?? null,
                schoolName: sch?.name ?? null,
                currency: sch?.currency ?? "UGX",
                firstName: l.first_name ?? "",
                lastName: l.last_name ?? "",
                fullName: `${l.first_name ?? ""} ${l.last_name ?? ""}`.trim() || "Learner",
                initials: initialsOf(l.first_name ?? "", l.last_name ?? ""),
                photoUrl: l.photo_url ?? null,
                gradeLabel,
                streamId: l.current_stream_id ?? null,
                admissionNumber: l.admission_number ?? null,
              };
            });
          }
        }

        if (!mounted) return;
        setCtx({ parentUserId: user.id, parentIds, children });
        setLoading(false);
      } catch (e) {
        if (mounted) { setError((e as Error).message); setLoading(false); }
      }
    })();
    return () => { mounted = false; };
  }, []);

  return { ctx, loading, error };
}
